import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';

import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSeasonalTooltipFormatter from '@/components/Chart/hooks/tooltip/useSeasonalTooltipFormatter';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { getDayMonthOfYear } from '@/components/Chart/utils/getDayMonthOfYear';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { regularChartPalette } from '@/constants/constants';

import useForwardCurveOptions from './useForwardCurveOptions';

import type { XAxisOptions } from 'highcharts';

const AREA_RANGE_COLOR = '#cdcdb5';
const AREA_RANGE_FILL_OPACITY = 0.2;
const AVERAGE_COLOR = '#F8F8F8';
const X_AXIS_TICK_INTERVAL = 31;
const X_AXIS_MIN = -5;

/**
 * Assigns color to a seasonal series based on its type and field.
 * - Area range series get a fixed muted color
 * - Average series get a light color
 * - Year-based series get a color derived from distance to current year
 * - All other series fall back to combinedIndex-based palette color
 */
const assignSeasonalSeriesColor = (series: ExtendedSeriesOptionsType): void => {
  const { combinedIndex, field } = series.custom;

  if (series.type === 'arearange') {
    series.color = AREA_RANGE_COLOR;
    series.fillOpacity = AREA_RANGE_FILL_OPACITY;
    return;
  }

  if (field === 'average') {
    series.color = AVERAGE_COLOR;
    return;
  }

  if (!Number.isNaN(Number(field))) {
    const yearToYearIndex = new Date().getFullYear() - Number(field);
    series.color = regularChartPalette[yearToYearIndex % regularChartPalette.length];
    return;
  }

  series.color = regularChartPalette[combinedIndex % regularChartPalette.length];
};

/**
 * Configures X-axis options for seasonal charts.
 * Sets tick interval, min value, and month-based label formatting.
 */
const configureSeasonalXAxis = (
  xAxisOptions: XAxisOptions,
  formatDateString: (date: string, variant: DateTextVariants) => string
): void => {
  xAxisOptions.tickInterval = X_AXIS_TICK_INTERVAL;
  xAxisOptions.min = X_AXIS_MIN;

  if (xAxisOptions.labels) {
    xAxisOptions.labels.formatter = function (): string {
      const value = Number(this.value);
      return formatDateString(getDayMonthOfYear(value + 1).toJSON(), DateTextVariants.MONTH);
    };
  }
};

const useSeasonalOptions = (height?: number) => {
  const { createOptions: createFvCurveOptions } = useForwardCurveOptions(height, true);
  const { createTooltipFormatter } = useSeasonalTooltipFormatter();
  const { formatDateString } = useDateText();
  const theme = useTheme();

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      chartSeries.forEach(assignSeasonalSeriesColor);

      const baseOptions = createFvCurveOptions(chartSeries, hiddenSeriesIds);
      const xAxisOptions = baseOptions.xAxis as XAxisOptions | undefined;

      if (xAxisOptions) {
        configureSeasonalXAxis(xAxisOptions, formatDateString);
      }

      return {
        ...baseOptions,
        xAxis: xAxisOptions,
        tooltip
      };
    },
    [createFvCurveOptions, formatDateString, tooltip]
  );

  return { createOptions };
};

export default useSeasonalOptions;
